import type { NodeModel } from '@minoru/react-dnd-treeview';

import type { CustomData } from './types';

export const ROOT_ID = 1;

export function createNode(parentId: NodeModel['parent'], type: CustomData['type']): NodeModel<CustomData> {
  return {
    id: `NEW_ELEMENT_${new Date().getTime()}`,
    parent: parentId,
    text: 'New Element ...',
    droppable: true,
    data: {
      uuid: `NEW_ELEMENT_${new Date().getTime()}`,
      type,
      changed: true,
      editable: true,
      isUnpublished: true,
      hasDraft: false,
      permissions: {
        // Newly created nodes can be edited and subnodes can be added
        canRename: true,
        canCreate: true,
        canDelete: true,
        // !BUT! Node has to exist in backend before page can be viewed and permissions can be altered (Menu has to be saved first!)
        canView: false,
        canManagePermissions: false,
      },
    },
  };
}

export function calcIndent(depth: number) {
  return `${(depth || 0) * 2}rem`;
}

export function flatten<T extends object & { children?: T[] }>(item: T) {
  return [item, ...(item?.children || []).flatMap(flatten)];
}

export function generateTreeDataFromMenuElements(
  elements: MenuElement[],
  isEditAllowed: boolean,
  userPermissions: { rootAccess: boolean; manageMenuElements: boolean; manageMenuGrants: boolean },
): NodeModel<CustomData>[] {
  const nodes: NodeModel<CustomData>[] = [
    {
      id: ROOT_ID,
      parent: 0,
      text: 'ROOT',
      droppable: true,
      data: {
        uuid: 'ROOT',
        type: 'link',
        editable: isEditAllowed && userPermissions.rootAccess, // Only users with Editor or Admin role are allowed to add elements on root level
        hasDraft: false, // ROOT can not have a draft!
        isUnpublished: false, // ROOT can not be unpublished!
        permissions: {
          canCreate: isEditAllowed && userPermissions.rootAccess, // Only users with Editor or Admin role are allowed to add elements on root level
          canManagePermissions: false, // ROOT has no permissions!
          canRename: false, // ROOT must not be renamed!
          canDelete: false, // ROOT must not be deleted!
          canView: false, // ROOT can not be viewed!
        },
      },
    },
    ...elements.flatMap(flatten).map((element): NodeModel<CustomData> => {
      const type = element.path ? 'page' : 'link';
      return {
        id: element.id,
        parent: element.parent || ROOT_ID,
        text: element.title,
        droppable: true,
        data: {
          uuid: element.id,
          type,
          editable:
            isEditAllowed &&
            (userPermissions.rootAccess ||
              (userPermissions.manageMenuElements && element.permissions?.update) ||
              false),
          hasDraft: element.hasDraft,
          isUnpublished: !element.isPublished,
          permissions: {
            canCreate:
              isEditAllowed &&
              ((userPermissions.manageMenuElements && element.permissions?.create) || userPermissions.rootAccess),
            canManagePermissions:
              isEditAllowed && (userPermissions.manageMenuGrants || element.permissions?.set_grants),
            canRename:
              isEditAllowed &&
              ((userPermissions.manageMenuElements && element.permissions?.update) || userPermissions.rootAccess),
            canDelete:
              isEditAllowed &&
              ((userPermissions.manageMenuElements && element.permissions?.delete) || userPermissions.rootAccess),
            canView:
              type === 'page' &&
              ((userPermissions.manageMenuElements && element.permissions?.view) || userPermissions.rootAccess),
          },
        },
      };
    }),
  ];

  return nodes;
}

// We dont want reference to artifical root node
export function sanitizeParentId(id: NodeModel['parent']) {
  return id !== ROOT_ID ? `${id}` : null;
}

export function sanitizeUuid(id: NodeModel['id']) {
  return `${id}`;
}
